import type { Doc, Id } from '../_generated/dataModel'
import type { MutationCtx, QueryCtx } from '../_generated/server'
import { breatherDayKeys } from './day'

// A frozen goal is paused: its tasks leave Today, its habits are hidden, and
// frozen days count as rest days so no streak breaks. Windows are kept after a
// thaw so those days stay rest days in streaks and dot grids.
export type FreezeWindow = { from: string; until?: string }

export function isGoalStarted(
  goal:
    | { startMode?: 'now' | 'date' | 'unsure'; startDate?: string; status?: string }
    | null
    | undefined,
  dayKey: string,
) {
  if (!goal || !goal.startMode || goal.startMode === 'now') return true
  if (goal.startMode === 'date') return !goal.startDate || goal.startDate <= dayKey
  // A user can start an unsure goal by moving it into progress; setGoalStatus
  // stamps that day into startDate so past habit days stay paused.
  return goal.status === 'in-progress'
}

export function isGoalFrozen(
  goal:
    | {
        freezes?: FreezeWindow[]
        startMode?: 'now' | 'date' | 'unsure'
        startDate?: string
        status?: string
      }
    | null
    | undefined,
  dayKey: string,
) {
  if (!isGoalStarted(goal, dayKey)) return true
  const w = goal?.freezes?.[goal.freezes.length - 1]
  return !!w && w.from <= dayKey && (!w.until || dayKey <= w.until)
}

export function goalFrozenDayKeys(
  goal: { freezes?: FreezeWindow[] } | null | undefined,
  upToKey: string,
  maxDays = 120,
): string[] {
  return (goal?.freezes ?? []).flatMap((w) => breatherDayKeys(w.from, w.until, upToKey, maxDays))
}

export function addDaysKey(key: string, days: number) {
  return new Date(Date.parse(`${key}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
}

// Freezing starts today; an earlier open window is closed first.
export function freezeWindows(prev: FreezeWindow[] | undefined, today: string, until?: string) {
  const kept = (prev ?? [])
    .filter((w) => w.from < today)
    .map((w) => (!w.until || w.until >= today ? { ...w, until: addDaysKey(today, -1) } : w))
  return [...kept, { from: today, ...(until ? { until } : {}) }]
}

// Thawing ends the current window yesterday (a window started today is dropped).
export function thawWindows(prev: FreezeWindow[] | undefined, today: string) {
  const ws = [...(prev ?? [])]
  const last = ws[ws.length - 1]
  if (!last || last.from > today || (last.until && last.until < today)) return ws
  if (last.from === today) ws.pop()
  else ws[ws.length - 1] = { ...last, until: addDaysKey(today, -1) }
  return ws
}

// Root goal of a node, memoised per query (habits mostly share a few goals).
export async function rootGoalCached(
  ctx: QueryCtx,
  node: Doc<'nodes'>,
  cache: Map<string, Doc<'nodes'> | null>,
): Promise<Doc<'nodes'> | null> {
  let cur: Doc<'nodes'> | null = node
  const seen: string[] = []
  for (let i = 0; i < 8 && cur; i++) {
    if (cur.type === 'goal') break
    const pid = cur.parentId as string | null
    if (!pid) {
      cur = null
      break
    }
    if (cache.has(pid)) {
      cur = cache.get(pid)!
      break
    }
    seen.push(pid)
    cur = await ctx.db.get(cur.parentId!)
  }
  const goal = cur?.type === 'goal' ? cur : null
  for (const id of seen) cache.set(id, goal)
  return goal
}

const RESTORE_TASKS = 2

// After a thaw, put the goal back on today's surface: a freeze that spanned a
// day boundary left it out of that day's pick. Adds up to RESTORE_TASKS of the
// active milestone's next open tasks that aren't already there.
export async function restoreGoalToToday(
  ctx: MutationCtx,
  userId: string,
  goalId: Id<'nodes'>,
  tzOffsetMinutes: number,
  logicalToday: string,
) {
  const calendarToday = new Date(Date.now() - tzOffsetMinutes * 60_000).toISOString().slice(0, 10)
  const surfaces = (
    await Promise.all(
      [...new Set([logicalToday, calendarToday])].map((date) =>
        ctx.db
          .query('dailySurface')
          .withIndex('by_user_date', (q) => q.eq('userId', userId).eq('date', date))
          .unique(),
      ),
    )
  ).filter((s): s is Doc<'dailySurface'> => !!s)
  if (!surfaces.length) return

  const milestones = (
    await ctx.db
      .query('nodes')
      .withIndex('by_parent', (q) => q.eq('parentId', goalId))
      .take(50)
  )
    .filter((m) => m.type === 'milestone' && m.completedAt == null && (m.progress ?? 0) < 100)
    .sort((a, b) => a._creationTime - b._creationTime)
  const active = milestones[0]
  if (!active) return
  const tasks: Doc<'nodes'>[] = []
  for (const target of await ctx.db
    .query('nodes')
    .withIndex('by_parent', (q) => q.eq('parentId', active._id))
    .take(50)) {
    if (target.type !== 'target') continue
    for (const t of await ctx.db
      .query('nodes')
      .withIndex('by_parent', (q) => q.eq('parentId', target._id))
      .take(100))
      if (t.type === 'task' && t.completedAt === null && !t.archivedAt && !t.isHabit) tasks.push(t)
  }
  tasks.sort((a, b) => (a.sortOrder ?? a._creationTime) - (b.sortOrder ?? b._creationTime))

  for (const surface of surfaces) {
    const present = new Set(surface.taskIds.map(String))
    // Tasks still listed (a same-day thaw) already reappear; only top up.
    const missing = RESTORE_TASKS - tasks.filter((t) => present.has(t._id)).length
    if (missing <= 0) continue
    const add = tasks.filter((t) => !present.has(t._id)).slice(0, missing)
    if (add.length)
      await ctx.db.patch(surface._id, { taskIds: [...surface.taskIds, ...add.map((t) => t._id)] })
  }
}
